// src/pages/AmlKycPolicy.jsx
import React from "react";
import { motion } from "framer-motion";

export default function AmlKycPolicy() {
  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-slate-900 pb-12 sm:pb-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero */}
      <header className="bg-white dark:bg-slate-800 shadow-md border-b-4 border-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-800 dark:text-slate-100 mb-2 sm:mb-3">
            🛡️ Coinzetto Anti-Money Laundering (AML) Policy
          </h1>
          <p className="text-blue-600 dark:text-slate-300 text-sm sm:text-base">
            Effective Date: 23 May 2025 &nbsp;|&nbsp; Last Updated: 5 July 2025
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 mt-8 sm:mt-12">
        <article className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border-t-4 border-blue-600 p-5 sm:p-8 space-y-8 sm:space-y-10 text-blue-800/90 dark:text-slate-200 leading-relaxed">
          {/* Intro */}
          <section className="text-[13px] sm:text-base">
            <p>
              Coinzetto Technologies Private Limited (“Coinzetto”, “we”, “our”, “us”) is committed to the highest
              standards of anti-money laundering (AML), counter-terrorism financing (CTF), and prevention of illicit
              activity. This AML Policy is designed to ensure that the Coinzetto Platform is not misused, and that we remain
              fully compliant with applicable Indian laws and international best practices.
            </p>
          </section>

          {/* 1. Scope */}
          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100 mb-2">
              1. Scope and Applicability
            </h2>
            <p className="text-[13px] sm:text-base">This AML Policy applies to:</p>
            <ul className="list-disc list-outside pl-5 sm:pl-6 space-y-1 text-[13px] sm:text-base">
              <li>All users who register and transact on the Coinzetto Platform</li>
              <li>
                All products and services, including crypto-INR trading, crypto-crypto trading, deposits, withdrawals,
                and any future derivatives products
              </li>
            </ul>
            <p className="mt-2 text-[13px] sm:text-base">
              Use of the Platform constitutes express acceptance of this AML Policy, our{" "}
              <a href="/terms" className="text-blue-700 dark:text-blue-300 underline underline-offset-2">
                Terms of Use
              </a>{" "}
              and{" "}
              <a href="/privacy" className="text-blue-700 dark:text-blue-300 underline underline-offset-2">
                Privacy Policy
              </a>
              .
            </p>
          </section>

          {/* 2. Objectives */}
          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100 mb-2">
              2. Objectives
            </h2>
            <ul className="list-disc list-outside pl-5 sm:pl-6 space-y-1 text-[13px] sm:text-base">
              <li>Prevent, detect, and report money-laundering or terrorism-financing activities</li>
              <li>Establish robust KYC procedures and continuous transaction monitoring</li>
              <li>Comply with PMLA 2002, FATF guidelines, and FIU-IND directives</li>
              <li>Co-operate fully with government, law-enforcement, and regulators</li>
            </ul>
          </section>

          {/* 3. KYC */}
          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100 mb-2">
              3. Know Your Customer (KYC)
            </h2>

            <p className="font-semibold text-blue-800 dark:text-slate-100 text-[13px] sm:text-base">
              a. User Identification &amp; Verification
            </p>
            <ul className="list-disc list-outside pl-5 sm:pl-6 space-y-1 text-[13px] sm:text-base">
              <li>Collection of PAN, Aadhaar, Passport, or other government-issued IDs</li>
              <li>Bank-account verification for INR flows</li>
              <li>Validation of name, address, date of birth, and contact details</li>
              <li>Selfie or video KYC as required</li>
            </ul>

            <p className="font-semibold text-blue-800 dark:text-slate-100 mt-4 text-[13px] sm:text-base">
              b. Ongoing KYC &amp; Enhanced Due Diligence
            </p>
            <ul className="list-disc list-outside pl-5 sm:pl-6 space-y-1 text-[13px] sm:text-base">
              <li>Periodic re-verification of identity data</li>
              <li>Source-of-funds checks for high-value users</li>
              <li>Enhanced Due Diligence (EDD) for high-risk profiles</li>
            </ul>
          </section>

          {/* 4. Risk Categorization */}
          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100 mb-2">
              4. Risk Categorization
            </h2>
            <p className="text-[13px] sm:text-base">
              Users are profiled as <em>low</em>, <em>medium</em>, or <em>high</em> risk based on trade volume, geography,
              occupation, and transaction behavior. High-risk users are subject to stricter monitoring.
            </p>
          </section>

          {/* 5. Transaction Monitoring */}
          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100 mb-2">
              5. Suspicious Transaction Monitoring
            </h2>
            <p className="text-[13px] sm:text-base">Automated and manual systems flag anomalies such as:</p>
            <ul className="list-disc list-outside pl-5 sm:pl-6 space-y-1 text-[13px] sm:text-base">
              <li>Sudden large deposits/withdrawals inconsistent with history</li>
              <li>Use of multiple accounts or obfuscated IP addresses</li>
              <li>Transfers to/from sanctioned or watch-listed addresses</li>
              <li>Trades involving privacy-enhanced coins or mixers</li>
            </ul>
          </section>

          {/* 6. Prohibited Jurisdictions */}
          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100 mb-2">
              6. Prohibited Jurisdictions &amp; Activities
            </h2>
            <p className="mb-1 text-[13px] sm:text-base">Access is denied to users from:</p>
            <ul className="list-disc list-outside pl-5 sm:pl-6 space-y-1 text-[13px] sm:text-base">
              <li>FATF high-risk/black-listed countries</li>
              <li>UN-, OFAC-, or India-sanctioned regions</li>
            </ul>
            <p className="mt-2 text-[13px] sm:text-base">
              Users must not use Coinzetto to fund illegal weapons, drugs, ransomware, or act as shell entities.
            </p>
          </section>

          {/* 7. Data Retention */}
          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100 mb-2">
              7. Data Retention
            </h2>
            <p className="text-[13px] sm:text-base">
              All KYC, trade, and financial records are retained for at least 5 years after account closure, or longer if
              required by law.
            </p>
          </section>

          {/* 8. Employee Training */}
          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100 mb-2">
              8. Employee Training &amp; Compliance
            </h2>
            <p className="text-[13px] sm:text-base">
              Relevant staff undergo periodic AML/CTF training. A Principal Officer and Compliance Officer oversee FIU
              reporting and internal escalations.
            </p>
          </section>

          {/* 9. Reporting */}
          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100 mb-2">
              9. Reporting Obligations (FIU-IND)
            </h2>
            <p className="text-[13px] sm:text-base">
              Coinzetto files Suspicious Transaction Reports (STRs) and any large-value reports required under PMLA with
              FIU-IND, and fully cooperates with law-enforcement.
            </p>
          </section>

          {/* 10. Enforcement */}
          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100 mb-2">
              10. Enforcement
            </h2>
            <p className="text-[13px] sm:text-base">
              Breach of this AML Policy may result in account suspension, fund freeze, or permanent closure, and will be
              reported to authorities where permitted by law.
            </p>
          </section>

          {/* 11. Contact */}
          <section>
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-slate-100 mb-2">
              11. Contact &amp; Grievances
            </h2>
            <p className="text-[13px] sm:text-base">
              Compliance Officer
              <br />
              Email:{" "}
              <a
                href="mailto:admin@coinzetto.com"
                className="text-blue-700 dark:text-blue-300 underline underline-offset-2"
              >
                admin@coinzetto.com
              </a>
              <br />
              Phone: +91&nbsp;8619&nbsp;03&nbsp;8619
              <br />
              Address: 4 Ram Nagar Colony, Sundar Nagar, Jaisinghpura Khor, Jaipur 302027
            </p>
          </section>
        </article>

        {/* Small footer hint for UX */}
        <p className="mt-4 text-center text-[11px] sm:text-xs text-blue-700/80 dark:text-slate-400">
          For detailed security practices and KYC FAQs, visit the Support page.
        </p>
      </main>
    </motion.div>
  );
}
